import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { useJourneyStore } from '@/stores/journeyStore'
import { 
  Send, 
  Lightbulb, 
  CheckCircle, 
  AlertCircle,
  MessageCircle,
  ArrowRight,
  Sparkles,
  Target,
  RefreshCw,
  Loader2,
  Home
} from 'lucide-react'
import { analyzePromptQuality, generateId } from '@/lib/utils'
import { ChatMessage } from '@/types'

interface PromptSuggestion {
  id: string
  category: 'specificity' | 'context' | 'clarity' | 'actionable'
  title: string
  before: string
  after: string
  explanation: string
}

const promptSuggestions: PromptSuggestion[] = [
  {
    id: '1',
    category: 'specificity',
    title: 'Be More Specific',
    before: 'Write something about marketing',
    after: 'Write a 200-word email marketing campaign for a local coffee shop\'s new seasonal drink launch',
    explanation: 'Specific prompts with clear parameters get much better results than vague requests.'
  },
  {
    id: '2',
    category: 'context',
    title: 'Provide Context',
    before: 'Help me with customer service',
    after: 'Act as a customer service expert for a small online retail business. How should I respond to a customer who received a damaged product?',
    explanation: 'Context helps AI understand your situation and provide more relevant advice.'
  },
  {
    id: '3',
    category: 'clarity',
    title: 'Ask Clear Questions',
    before: 'Business stuff',
    after: 'What are 5 ways I can improve customer retention for my consulting business?',
    explanation: 'Clear questions with specific numbers or formats make responses more actionable.'
  },
  {
    id: '4',
    category: 'actionable',
    title: 'Request Actionable Steps',
    before: 'Tell me about social media',
    after: 'Create a 7-day social media posting schedule for a fitness trainer, including post types and optimal timing',
    explanation: 'Asking for structured, actionable outputs gives you practical results you can implement immediately.'
  }
]

export function Stage1Chat() {
  const navigate = useNavigate()
  const { completeStage, updateStageProgress } = useJourneyStore()
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: generateId(),
      content: "Hello! I'm your AI assistant for this first stage. I'm here to help you learn the fundamentals of effective AI interaction. Try asking me anything - and I'll provide feedback on how to improve your prompts!",
      role: 'assistant',
      timestamp: new Date(),
      stageId: 1
    }
  ])
  const [currentPrompt, setCurrentPrompt] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const [completedExercises, setCompletedExercises] = useState<string[]>([])
  const [currentSuggestion, setCurrentSuggestion] = useState(0)
  const [promptCount, setPromptCount] = useState(0)
  const [confidenceLevel, setConfidenceLevel] = useState(5)
  const [completionMessage, setCompletionMessage] = useState('')
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const simulateAIResponse = async (prompt: string): Promise<string> => {
    // Simulate AI processing time
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000))
    
    const quality = analyzePromptQuality(prompt)
    
    // Generate contextual responses based on prompt quality and content
    if (quality >= 8) {
      return `Excellent prompt! Here's a comprehensive response to "${prompt}"\n\nI can see you've included specific details, clear context, and a well-defined request. This is exactly the kind of prompt that gets great results from AI tools.\n\n[This would be followed by a detailed, helpful response to your actual question]\n\n✨ Your prompt scored ${quality}/10 for quality!`
    } else if (quality >= 6) {
      return `Good prompt! For "${prompt}"\n\n[Here's my response to your question]\n\nTo make this even better, try adding more specific details about what exactly you need, or provide more context about your situation.\n\n📈 Your prompt scored ${quality}/10 - nice improvement!`
    } else if (quality >= 4) {
      return `I can help with "${prompt}"\n\n[Basic response provided]\n\nTip: This prompt could be improved by being more specific. Instead of general requests, try asking for specific formats, numbers, or steps.\n\n💡 Your prompt scored ${quality}/10 - let's work on making it more specific!`
    } else {
      return `I'd love to help, but "${prompt}" is quite vague.\n\nHere are some ways to improve this prompt:\n• Be more specific about what you need\n• Provide context about your situation\n• Ask for a particular format or number of items\n• Include relevant details\n\n🎯 Your prompt scored ${quality}/10 - try the suggestions above for better results!`
    }
  }

  const handleSendMessage = async () => {
    if (!currentPrompt.trim() || isLoading) return

    const userMessage: ChatMessage = {
      id: generateId(),
      content: currentPrompt,
      role: 'user',
      timestamp: new Date(),
      stageId: 1,
      metadata: {
        promptQuality: analyzePromptQuality(currentPrompt)
      }
    }

    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)
    setCurrentPrompt('')
    setPromptCount(prev => prev + 1)

    try {
      const response = await simulateAIResponse(currentPrompt)
      
      const assistantMessage: ChatMessage = {
        id: generateId(),
        content: response,
        role: 'assistant',
        timestamp: new Date(),
        stageId: 1,
        metadata: {
          responseTime: 2000
        }
      }

      setMessages(prev => [...prev, assistantMessage])
      
      // Update progress
      updateStageProgress(1, {
        interactionData: { promptCount, averageQuality: userMessage.metadata?.promptQuality }
      })

      // Check if user should advance confidence
      if (userMessage.metadata?.promptQuality && userMessage.metadata.promptQuality >= 7) {
        setConfidenceLevel(prev => Math.min(10, prev + 1))
      }

    } catch (error) {
      console.error('Error generating response:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUseSuggestion = (suggestion: PromptSuggestion) => {
    setCurrentPrompt(suggestion.after)
    setCompletedExercises(prev => [...prev, suggestion.id])
    inputRef.current?.focus()
  }

  const handleCompleteStage = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage('Completing stage...')
    
    try {
      const stageData = {
        promptCount,
        completedExercises: completedExercises.length,
        finalConfidence: confidenceLevel,
        chatHistory: messages.length
      }
      
      completeStage(1, stageData)
      setCompletionMessage('Stage completed! Moving to next stage...')
      
      // Let the store's auto-advancement handle navigation
      // No immediate navigation to dashboard
    } catch (error) {
      console.error('Error completing stage:', error)
      setCompletionMessage('Error completing stage. Please try again.')
      setIsCompleting(false)
    }
  }

  const handleSkipStage = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage('Skipping to next stage...')
    
    try {
      // Mark as completed but with minimal data for skip
      const stageData = {
        skipped: true,
        promptCount,
        completedExercises: completedExercises.length,
        finalConfidence: confidenceLevel
      }
      
      completeStage(1, stageData)
      
      // Let the store's auto-advancement handle navigation
      // No immediate navigation to dashboard
    } catch (error) {
      console.error('Error skipping stage:', error)
      setCompletionMessage('Error skipping stage. Please try again.')
      setIsCompleting(false)
    }
  }

  const canComplete = promptCount >= 1 || completedExercises.length >= 1

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Stage Header */}
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          <MessageCircle className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Stage 1: First AI Conversation</h1>
          <p className="text-lg text-muted-foreground">Learn to communicate effectively with AI through hands-on practice</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Chat Interface */}
        <div className="lg:col-span-2">
          <Card className="h-[600px] flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                AI Chat Interface
              </CardTitle>
              <CardDescription>
                Practice your prompting skills with real-time feedback
              </CardDescription>
            </CardHeader>
            
            <CardContent className="flex-1 flex flex-col">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-4 p-4 bg-muted/20 rounded-lg">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      message.role === 'user' 
                        ? 'bg-primary text-primary-foreground ml-auto' 
                        : 'bg-background border'
                    }`}>
                      <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                      {message.metadata?.promptQuality && (
                        <div className="text-xs mt-2 opacity-70 flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          Quality: {message.metadata.promptQuality}/10
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex gap-3 justify-start">
                    <div className="bg-background border rounded-lg p-3 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span className="text-sm">AI is thinking...</span>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={currentPrompt}
                  onChange={(e) => setCurrentPrompt(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your prompt here... (try to be specific!)"
                  className="flex-1 px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={isLoading}
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={!currentPrompt.trim() || isLoading}
                  className="gap-2"
                >
                  <Send className="w-4 h-4" />
                  Send
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Guidance Panel */}
        <div className="space-y-6">
          {/* Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Messages sent:</span>
                <span className="font-medium">{promptCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Exercises completed:</span>
                <span className="font-medium">{completedExercises.length}/4</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm">Confidence level:</span>
                <span className="font-medium">{confidenceLevel}/10</span>
              </div>
              
              <div className="space-y-2 mt-4">
                {completionMessage && (
                  <div className="text-center p-2 bg-primary/10 text-primary rounded-md text-sm">
                    {completionMessage}
                  </div>
                )}
                
                {canComplete && (
                  <Button 
                    onClick={handleCompleteStage} 
                    disabled={isCompleting}
                    className="w-full gap-2"
                  >
                    {isCompleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>Complete Stage <ArrowRight className="w-4 h-4" /></>
                    )}
                  </Button>
                )}
                
                <Button 
                  onClick={handleSkipStage} 
                  variant="outline" 
                  disabled={isCompleting}
                  className="w-full gap-2"
                >
                  {isCompleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>Skip to Next Stage <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Current Exercise */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-600" />
                Practice Exercise
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentSuggestion < promptSuggestions.length ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">{promptSuggestions[currentSuggestion].title}</h4>
                    <div className="space-y-2">
                      <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                        <div className="text-xs text-red-600 dark:text-red-400 mb-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Before (Poor):
                        </div>
                        <div className="text-sm text-red-800 dark:text-red-200">
                          "{promptSuggestions[currentSuggestion].before}"
                        </div>
                      </div>
                      
                      <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                        <div className="text-xs text-green-600 dark:text-green-400 mb-1 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          After (Better):
                        </div>
                        <div className="text-sm text-green-800 dark:text-green-200">
                          "{promptSuggestions[currentSuggestion].after}"
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mt-2">
                      {promptSuggestions[currentSuggestion].explanation}
                    </p>
                    
                    <Button 
                      onClick={() => handleUseSuggestion(promptSuggestions[currentSuggestion])}
                      variant="outline" 
                      size="sm" 
                      className="mt-3 w-full"
                    >
                      Try This Example
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-2">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto" />
                  <div className="text-sm">All exercises completed!</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Tips</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                  Be specific about what you want
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                  Provide context about your situation
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                  Ask for specific formats or numbers
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                  Include relevant details
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}